/**
 * 시뮬레이션 관리(목록) 화면의 정본 모델 — SimulationsPage 에서 분리(P4-c). 렌더 무관 순수.
 * 카드 항목 타입·상태 묶음·정렬 옵션·목록 파생(카드 조립·필터·정렬·건수) 규칙이 여기 있다. 상태의 낱말·색은 상태 표현 정본(runStatusView).
 */
import type { OwnedSession } from '@/stubs/session/session_directory_pb';
import type { SessionSimulationState } from '@/stubs/sm/simulation_manager_pb';
import type { SimulationEntry } from '@/types/project';
import type { SortOrder } from '@/shared/ui/GridSortHeader';
import { runEntryName } from '@/shared/runName';
import { WORKSPACE_ALL, WORKSPACE_DELETED } from '@/app/workspaceFilter';
import { ROWLESS_PROJECTS } from '@/app/quickRunProject';
import { smSimInProgress } from '@/app/services/sm/taskStatus';
import { SIM_STATUSES, isInProgressSimStatus, type SimStatus } from '@/types/simulation';
import { runStatusAdjective, runStatusLabel } from '@/shared/ui/runStatusView';

/**
 * 실행의 소속 프로젝트 — 이름은 적재된 프로젝트(현재 이름, 개명 반영) → 이력의 스냅샷 순.
 * `deleted` 는 프로젝트 목록이 준비된 뒤 그 목록에 없을 때만 참 — 목록이 오기 전엔 단정하지 않는다.
 */
export interface RunProjectRef {
  id: string;
  name?: string;
  deleted: boolean;
}

export function resolveRunProject(
  projectId: string | undefined,
  snapshotName: string | undefined,
  projectNames: Map<string, string>,
  projectsReady: boolean,
): RunProjectRef | undefined {
  if (!projectId) return undefined;
  const live = projectNames.get(projectId);
  return { id: projectId, name: live ?? (snapshotName || undefined), deleted: projectsReady && live === undefined };
}

/**
 * 실행의 소속을 풀 프로젝트 이름표·작업 공간표 — 프로젝트 목록에 행 없는 프로젝트(퀵런)를 더한다. 퀵런 실행이
 * «삭제된 프로젝트» 로 읽히지 않게(스테이지 2026-10-02: «삭제된 프로젝트 · 퀵 시뮬레이션», 삭제된 프로젝트 묶음).
 */
export function runProjectNames(projects: readonly { id: string; name: string }[]): Map<string, string> {
  // 행 없는 프로젝트를 먼저 두고 목록이 덮는다 — 같은 id 의 실제 행이 있으면 그 행이 이긴다
  return new Map([...[...ROWLESS_PROJECTS].map(([id, p]) => [id, p.name] as const), ...projects.map((p) => [p.id, p.name] as const)]);
}
/** 행 없는 프로젝트(퀵런)는 그 사용자의 기본 워크스페이스 행에 속한다 — 기본 행을 모르면(목록 조회 실패) 넣지 않는다(삭제 버킷으로 읽힌다) */
export function runProjectWorkspaces(projects: readonly { id: string; workspace_id: string }[], defaultWorkspaceId: string | null): Map<string, string> {
  const rowless = defaultWorkspaceId ? [...ROWLESS_PROJECTS.keys()].map((id) => [id, defaultWorkspaceId] as const) : [];
  return new Map([...rowless, ...projects.map((p) => [p.id, p.workspace_id] as const)]);
}

export const DELETED_PROJECT_LABEL = '삭제된 프로젝트';
/** 결과 보기의 안내 — 프로젝트(저작물)는 없고 실행 기록은 남았다. 결과가 있는지는 단정하지 않고 실제로 받아 본다
 *  (프로젝트 삭제는 실행 폴더를 남기므로 결과는 그대로 열린다. 예전의 프로젝트 삭제가 결과까지 지운 실행은 수집기가 «결과 없음» 을
 *  말한다) */
export const DELETED_PROJECT_RESULTS_NOTICE =
  '삭제된 프로젝트의 실행입니다 — 실행 기록과 남아 있는 결과만 보입니다.';

/** 화면에 보이는 프로젝트 이름 — UUID 는 절대 라벨이 되지 않는다 */
export function runProjectLabel(ref: RunProjectRef): string {
  if (ref.deleted) return ref.name ? `${DELETED_PROJECT_LABEL} · ${ref.name}` : DELETED_PROJECT_LABEL;
  return ref.name ?? '';
}

/** PROJECTS 면의 라벨 — 카드가 푼 소속을 그대로 쓴다(삭제된 프로젝트의 스냅샷 이름 포함) */
export function projectLabels(cards: readonly CardItem[]): Map<string, string> {
  const labels = new Map<string, string>();
  for (const c of cards) if (c.project && !labels.has(c.project.id)) labels.set(c.project.id, runProjectLabel(c.project));
  return labels;
}

/** 실행이 있는 모든 프로젝트의 라벨 — 필터와 무관하게 같은 해석기로(카드를 다시 조립하지 않는다) */
export function resolveProjectLabels(
  input: Pick<CardBuildInput, 'visibleRuns' | 'simMeta' | 'visibleHistory' | 'projectNames' | 'projectsReady'>,
): Map<string, string> {
  const labels = new Map<string, string>();
  const put = (ref: RunProjectRef | undefined) => { if (ref && !labels.has(ref.id)) labels.set(ref.id, runProjectLabel(ref)); };
  for (const { sim } of input.visibleRuns) put(resolveRunProject(input.simMeta[sim.simId]?.projectId, undefined, input.projectNames, input.projectsReady));
  for (const e of input.visibleHistory) put(resolveRunProject(e.projectId, e.projectName, input.projectNames, input.projectsReady));
  return labels;
}

/**
 * 상태 묶음 — «전체»(상태가 아니다)와 상태 하나씩. 묶음은 상태 어휘(types/simulation)에서 나온다: «실행 중» 은 도는 실행(세션 카드 —
 * 짓는 중·일시정지 포함, 진행 중 상태를 대표한다), 그 뒤로 끝난 기록이 가질 수 있는 상태(어휘에서 진행 중을 뺀 것, «상태 모름» 포함).
 * 상태가 늘면 묶음·라벨·건수·빈 목록 문구가 따라 는다. 손으로 적는 것은 «전체» 와 실행 중의 대표뿐이다.
 */
export type GroupKey = 'all' | SimStatus;
export const STATUS_GROUPS: readonly GroupKey[] = ['all', 'running', ...SIM_STATUSES.filter((s) => !isInProgressSimStatus(s))];

/** 사이드바 상태 묶음의 라벨 — 상태 표현 정본의 낱말, 카드 칩·빈 목록 문구와 같다 */
export function groupLabel(group: GroupKey): string {
  return group === 'all' ? 'All' : runStatusLabel(group);
}
export type SortKey = 'recent' | 'title';

/** 정렬 옵션 — 픽커(ProjectGrid)와 같은 문법: 필드 Select + 방향 토글 */
export const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'recent', label: '시작 시각' },
  { value: 'title', label: '제목' },
];

/** 실행 중 카드 = 활성 sim + 소속 세션 + 부가 메타, 완료 카드 = 이력 행 — 한 그리드에서 렌더 */
export interface RunningCard {
  kind: 'running';
  session: OwnedSession;
  sim: SessionSimulationState;
  title?: string;
  projectId?: string;
  /** 소속 프로젝트(이름·삭제 여부) — 표시는 runProjectLabel 로만 */
  project?: RunProjectRef;
  /** 정렬용 — 실행 시작 시각(ms), 이력 미해석 시 세션 생성 시각 폴백 */
  /** 시각(epoch ms) — 알 수 없으면 undefined(표기는 '-', 정렬은 맨 뒤). 0 같은 대용값을 만들지 않는다 */
  at?: number;
}
export interface FinishedCard {
  kind: 'finished';
  entry: SimulationEntry;
  /** 소속 프로젝트(이름·삭제 여부) — 표시는 runProjectLabel 로만 */
  project?: RunProjectRef;
  /** 시각(epoch ms) — 알 수 없으면 undefined(표기는 '-', 정렬은 맨 뒤). 0 같은 대용값을 만들지 않는다 */
  at?: number;
}
export type CardItem = RunningCard | FinishedCard;

/** 카드가 부르는 실행 이름 — 카드 머리·이름 검색·제목 정렬이 같은 글을 쓴다(규칙은 runEntryName). 실행 중 카드는 이력을 아직
 *  읽지 못했으면 제목이 없다 — 빈 제목 기록과 같이 짧게 보인 실행 id 로 부른다 */
export function cardName(card: CardItem): string {
  return card.kind === 'running' ? runEntryName({ id: card.sim.simId, name: card.title ?? '' }) : runEntryName(card.entry);
}

export type SimMeta = Record<string, { title?: string; projectId?: string; at?: number }>;
export type ActiveRun = { session: OwnedSession; sim: SessionSimulationState };

export const POLL_INTERVAL_MS = 10_000;
/** 미지 프로젝트 재적재 시도 상한 — 초과 후에도 미지면 소속 미확인 숨김이 안전선 */
export const UNKNOWN_PROJECT_RELOAD_MAX_ATTEMPTS = 3;

export function sessionCreatedMs(session: OwnedSession): number {
  return session.createdAt ? Number(session.createdAt.toDate().getTime()) : 0;
}

/** 실행 중 실행 = 세션×활성 sim 평탄화 — 카드·집계·필터의 공통 원천. 비활성 sim 은 이력 카드가 담당(중복 방지) */
export function flattenActiveRuns(sessions: OwnedSession[] | null): ActiveRun[] {
  return (sessions ?? []).flatMap((session) =>
    (session.state?.simulations ?? []).filter((sim) => smSimInProgress(sim.status)).map((sim) => ({ session, sim })),
  );
}

export interface CardBuildInput {
  visibleRuns: ActiveRun[];
  simMeta: SimMeta;
  visibleHistory: readonly SimulationEntry[];
  projectNames: Map<string, string>;
  /** 프로젝트 목록 적재 완료 — 그 전엔 어떤 실행도 '삭제됨'으로 단정하지 않는다 */
  projectsReady: boolean;
  projectWorkspaces: Map<string, string>;
  group: GroupKey;
  workspaceFilter: string;
  projectFilter: string | null;
  query: string;
  sort: SortKey;
  sortOrder: SortOrder;
}

/** 카드 조립 → 상태 그룹·워크스페이스·프로젝트·검색 필터 → 정렬 */
export function buildCards(input: CardBuildInput): CardItem[] {
  const { visibleRuns, simMeta, visibleHistory, projectNames, projectWorkspaces, projectsReady, group, workspaceFilter, projectFilter, query, sort, sortOrder } = input;
  const running: CardItem[] = visibleRuns.map(({ session, sim }) => {
    const meta = simMeta[sim.simId] ?? {};
    return {
      kind: 'running',
      session,
      sim,
      title: meta.title,
      projectId: meta.projectId,
      project: resolveRunProject(meta.projectId, undefined, projectNames, projectsReady),
      at: meta.at ?? sessionCreatedMs(session),
    };
  });
  const finished: CardItem[] = visibleHistory.map((entry) => ({
    kind: 'finished',
    entry,
    project: resolveRunProject(entry.projectId, entry.projectName, projectNames, projectsReady),
    at: Number.isFinite(Date.parse(entry.timestamp)) ? Date.parse(entry.timestamp) : undefined,
  }));

  // 상태 필터는 카드가 실제로 표시하는 상태로 건다 — 종전에는 'completed'가 "종결된 것 전부"(중지·실패 포함)를
  // 뜻해 그룹 이름과 칩 라벨이 같은 단어로 다른 뜻을 가리켰다. 라이브 카드(SM 세션)는 «실행 중» 묶음에 속한다.
  let all: CardItem[] = [];
  if (group === 'all' || group === 'running') all = all.concat(running);
  if (group === 'all') {
    all = all.concat(finished);
  } else {
    all = all.concat(finished.filter((c) => c.kind === 'finished' && c.entry.status === group));
  }

  if (workspaceFilter !== WORKSPACE_ALL) {
    all = all.filter((c) => c.project !== undefined && workspaceOfProject(c.project.id, projectWorkspaces) === workspaceFilter);
  }
  if (projectFilter) {
    all = all.filter((c) => (c.kind === 'running' ? c.projectId : c.entry.projectId) === projectFilter);
  }
  const q = query.trim().toLowerCase();
  if (q) {
    all = all.filter((c) => {
      return cardName(c).toLowerCase().includes(q) || (c.project?.name ?? '').toLowerCase().includes(q);
    });
  }
  const direction = sortOrder === 'asc' ? 1 : -1;
  all.sort((a, b) => {
    if (sort === 'title') {
      return direction * cardName(a).localeCompare(cardName(b), 'ko');
    }
    return direction * ((a.at ?? 0) - (b.at ?? 0));
  });
  return all;
}

/** 이력의 상태별 건수(상태 어휘 전부). 모르는 이력(null)의 건수는 모른다(null) */
export function countHistoryByStatus(visibleHistory: readonly SimulationEntry[] | null): Record<SimStatus, number> | null {
  if (visibleHistory === null) return null;
  const counts = Object.fromEntries(SIM_STATUSES.map((s) => [s, 0])) as Record<SimStatus, number>;
  for (const e of visibleHistory) counts[e.status] += 1;
  return counts;
}

/**
 * 사이드바 상태 묶음 — 묶음마다 [키, 라벨, 건수]. «전체» 는 도는 실행 + 끝난 기록, «실행 중» 은 도는 실행(세션 카드 — 끝난 기록은 진행 중을
 * 담지 않는다: useRunInventory 가 거른다), 그 밖은 끝난 기록의 그 상태. 끝난 기록을 모르면(null) 그 수도 모른다(null)
 */
export function statusGroupCounts(liveCount: number, historyByStatus: Record<SimStatus, number> | null): Array<[GroupKey, string, number | null]> {
  const historyCount = historyByStatus && Object.values(historyByStatus).reduce((a, b) => a + b, 0);
  const countOf = (group: GroupKey): number | null =>
    group === 'all' ? (historyCount === null ? null : liveCount + historyCount)
    : group === 'running' ? liveCount
    : historyByStatus && historyByStatus[group];
  return STATUS_GROUPS.map((group) => [group, groupLabel(group), countOf(group)]);
}

export function emptyMessageFor(group: GroupKey, query: string): string {
  if (query) return '검색 결과가 없습니다.';
  if (group === 'all') return '표시할 실행이 없습니다. 프로젝트에서 해석을 시작해 보세요.';
  // 상태 낱말은 상태 표현 정본의 관형형 — 라벨·배지와 같은 말. 도는 것은 시뮬레이션, 끝난 것은 실행
  return `${runStatusAdjective(group)} ${isInProgressSimStatus(group) ? '시뮬레이션' : '실행'}이 없습니다.`;
}

/** 프로젝트별 실행 수 — 사이드바 Projects 섹션(피커의 워크스페이스 목록 대칭). 끝난 기록을 모르면(null) 수도 모른다(null) */
export function countRunsByProject(visibleRuns: ActiveRun[], simMeta: SimMeta, visibleHistory: readonly SimulationEntry[] | null): Map<string, number> | null {
  if (visibleHistory === null) return null;
  const counts = new Map<string, number>();
  for (const { sim } of visibleRuns) {
    const pid = simMeta[sim.simId]?.projectId;
    if (pid) counts.set(pid, (counts.get(pid) ?? 0) + 1);
  }
  for (const e of visibleHistory) {
    if (e.projectId) counts.set(e.projectId, (counts.get(e.projectId) ?? 0) + 1);
  }
  return counts;
}

/** 프로젝트의 워크스페이스 스코프 키 — 목록에 없는(삭제된) 프로젝트는 자기 버킷 */
export function workspaceOfProject(projectId: string, projectWorkspaces: Map<string, string>): string {
  return projectWorkspaces.get(projectId) ?? WORKSPACE_DELETED;
}

/** 워크스페이스별 실행 수 — 프로젝트 실행 수를 소속별로 합산(삭제 버킷 포함). 모르는 수(null)는 모른다 */
export function countRunsByWorkspace(projectRunCounts: Map<string, number> | null, projectWorkspaces: Map<string, string>): Map<string, number> | null {
  if (projectRunCounts === null) return null;
  const counts = new Map<string, number>();
  for (const [pid, n] of projectRunCounts) {
    const wid = workspaceOfProject(pid, projectWorkspaces);
    counts.set(wid, (counts.get(wid) ?? 0) + n);
  }
  return counts;
}

/** PROJECTS 섹션은 선택된 워크스페이스 스코프만 보여준다(B안 계단식) */
export function scopeProjectRunCounts(projectRunCounts: Map<string, number> | null, projectWorkspaces: Map<string, string>, workspaceFilter: string): Map<string, number> | null {
  if (projectRunCounts === null || workspaceFilter === WORKSPACE_ALL) return projectRunCounts;
  const scoped = new Map<string, number>();
  for (const [pid, n] of projectRunCounts) {
    if (workspaceOfProject(pid, projectWorkspaces) === workspaceFilter) scoped.set(pid, n);
  }
  return scoped;
}
